import { useState, MouseEvent, ChangeEvent, useEffect, ReactNode } from "react";
import { FilterBarType } from "../FilterBar";
import FilterBarBtn from "./FilterBarBtn";
import { ReactComponent as TagsIcon } from '../../../assets/Icons/adornments/Tag.svg';
import { ReactComponent as ClearIcon } from '../../../assets/Icons/adornments/Close.svg';
import { ClickAwayListener, Grow, Paper, Popper } from "@mui/material";
import FilterSearchBar from "./FilterSearchBar";
import { Dayjs } from "dayjs";
import { useTheme } from '@mui/material/styles';
import TagsFixedList from "../TagsFixedList";

export default function Tags({
    filter,
    setFilterValue,
    anchorEl,
    handleOpen,
    handleClose
}: {
    filter: {
        filters: FilterBarType[],
        groupKey?: string,
        value: string | string[] | { [key: string]: Dayjs | null } | null,
        icon?: ReactNode,
    },
    setFilterValue: (filters: FilterBarType[], value: string | string[] | { [key: string]: Dayjs | null } | null) => void,
    anchorEl: null | HTMLElement,
    handleOpen: (event: MouseEvent<HTMLButtonElement>) => void,
    handleClose: () => void,
}) {
    const [ selectedTags, setSelectedTags ] = useState<string[]>([]);
    const [ filteredOptions, setFilteredOptions ] = useState<string[]>([]);
        
    useEffect(() => {
        if (filter.value) {
            setSelectedTags(filter.value as string[])
        } else {
            setSelectedTags([])
        }
    }, [filter.value]);

    useEffect(() => {
        if (filter.filters[0].options) {
            setFilteredOptions(filter.filters[0].options);
        }
    }, [filter.filters]);

    const searchFn = (searchQuery: string) => {
        if (filter.filters[0].options) {
            const options = filter.filters[0].options;
            setFilteredOptions(options.filter(opt => opt.toLocaleLowerCase().includes(searchQuery.toLocaleLowerCase())));
        }
    };
        

    const handleCheck = (e: ChangeEvent<HTMLInputElement>) => {
        const name = e.target.name;
        const checked = e.target.checked;

        if (checked) {
            setSelectedTags(selectedTags.concat(name))
        } else {
            setSelectedTags(selectedTags.filter(tag => tag !== name))
        }

    };

    const removeTag = (e: MouseEvent<HTMLButtonElement>, name: string) => {
        e.preventDefault();

        setSelectedTags(selectedTags.filter(tag => tag !== name));
    }

    const applyTags = (e: MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();

        setFilterValue(filter.filters, selectedTags.length > 0 ? selectedTags : null);
        handleClose();
    };

    const undoTags = () => {
        handleClose();
        setSelectedTags(filter.value ? filter.value as string[] : []);
    }

    const resetTags = () => {
        handleClose();
        setSelectedTags([]);
        setFilterValue(filter.filters, null);
    }

    const theme = useTheme();
    

    return (
        <>
            <FilterBarBtn 
                label={filter.filters[0].label}
                value={filter.value as string | string[] | null}
                icon={filter.icon ?? <TagsIcon />}
                isOpen={Boolean(anchorEl && anchorEl.id === filter.filters[0].param)}
                handleOpen={handleOpen}
                handleClose={handleClose}
                resetFn={resetTags}
                id={filter.filters[0].param}
            />
            <Popper open={Boolean(anchorEl && anchorEl.id === filter.filters[0].param)} anchorEl={anchorEl} placement="bottom-start" sx={{ zIndex: 2000 }} transition>
                {({ TransitionProps }) => (
                <Grow {...TransitionProps} in={Boolean(anchorEl && anchorEl.id === filter.filters[0].param)} style={{ transformOrigin: '0 0 0' }}>
                <Paper
                    sx={{
                        borderRadius: '8px',
                        overflow: 'hidden',
                        marginTop: '8px',
                        boxShadow: theme.shadows[8],
                    }}
                >
                    <ClickAwayListener onClickAway={undoTags}>
                        <div className="w-full flex flex-col justify-start items-stretch bg-white max-w-[350px]">
                            <div className="w-full flex flex-col justify-start items-stretch p-4 gap-3 border-b border-light-outline border-dashed">
                                <FilterSearchBar 
                                    label="Search Tags"
                                    searchFn={searchFn}
                                />

                                {selectedTags.length > 0 && (
                                <div className="w-full flex flex-wrap justify-start items-start gap-2">
                                    {selectedTags.map(value => (
                                        <div className="rounded-lg bg-secondary text-primary px-2 py-1 flex justify-start items-center gap-1">
                                            <p className="text-[10px] font-semibold">{value}</p>
                                            <button onClick={(e:any) => removeTag(e, value)} className="w-[18px]"><ClearIcon/></button>
                                        </div>
                                    ))}
                                </div>
                                )}
                            </div>

                            <TagsFixedList 
                                filteredOptions={filteredOptions}
                                selectedTags={selectedTags}
                                handleCheck={handleCheck}
                            />

                            <div className="flex justify-end items-center gap-2 p-4 border-t border-light-outline">
                                <button onClick={resetTags} className="px-4 py-3 text-primary font-medium text-[12px]">Reset</button>
                                <button onClick={applyTags} className="px-4 py-3 text-white font-medium bg-primary rounded-lg text-[12px]">Apply</button>
                            </div>
                        </div>
                    </ClickAwayListener>
                </Paper>
                </Grow>
                )}
            </Popper>
        </>
    )
}